import { useState } from "react";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Loader2, Mail } from "lucide-react";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import { formatDate } from "@project/lib/date-format";
import type { PickupUpdatePayload } from "@project/types/item-donation";

function isPickupEmailSent(row: {
  donor_sent?: boolean | null;
  pickup_date?: string | null;
  pickup_note?: string | null;
}) {
  if (row.donor_sent === true) {
    return true;
  }
  // List endpoints may not include donor_sent; pickup fields are set after a successful send.
  if (row.donor_sent === false) {
    return false;
  }
  return Boolean(row.pickup_date?.trim() && row.pickup_note?.trim());
}

export function DonationPickupUpdateAction({
  row,
  label,
  onSendPickupUpdate,
  onComplete,
}: {
  row: {
    id: number;
    email?: string | null;
    pickup_note?: string | null;
    pickup_date?: string | null;
    donor_sent?: boolean | null;
  };
  label: string;
  onSendPickupUpdate: (
    id: number,
    payload: PickupUpdatePayload
  ) => Promise<{ donor_sent?: boolean | null } | undefined>;
  onComplete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pickupDate, setPickupDate] = useState("");
  const [pickupNote, setPickupNote] = useState("");
  const [acting, setActing] = useState(false);
  const [sentLocally, setSentLocally] = useState(false);

  const alreadySent = sentLocally || isPickupEmailSent(row);

  const openDialog = () => {
    if (alreadySent) {
      return;
    }
    setPickupDate(row.pickup_date ?? "");
    setPickupNote(row.pickup_note ?? "");
    setOpen(true);
  };

  const confirm = async () => {
    const date = pickupDate.trim();
    const note = pickupNote.trim();
    if (!date) {
      projectToast.error("Pickup date is required.");
      return;
    }
    if (!note) {
      projectToast.error("Pickup note is required.");
      return;
    }
    setActing(true);
    try {
      const result = await onSendPickupUpdate(row.id, {
        pickup_date: date,
        pickup_note: note,
      });
      if (result?.donor_sent === false) {
        projectToast.error(
          "Pickup details saved, but the donor email could not be sent."
        );
        onComplete();
        return;
      }
      if (result?.donor_sent === true) {
        setSentLocally(true);
      }
      projectToast.success("Pickup update email sent successfully.");
      setOpen(false);
      onComplete();
    } catch (err) {
      projectToast.error(
        getApiErrorMessage(err, "Could not send pickup update.")
      );
    } finally {
      setActing(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        disabled={alreadySent}
        className="text-sky-600 hover:text-sky-700 disabled:opacity-40"
        title={
          alreadySent
            ? "Pickup update email already sent"
            : row.email
              ? "Send pickup update email"
              : "Send pickup update email (donor email optional)"
        }
        aria-label={
          alreadySent
            ? `Pickup update already sent for ${label}`
            : `Send pickup update for ${label}`
        }
        onClick={openDialog}
      >
        <Mail className="h-4 w-4" />
      </Button>

      <Dialog
        open={open}
        onOpenChange={next => {
          if (!next && !acting) {
            setOpen(false);
          }
        }}
      >
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">
              Send pickup update email?
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-muted/40 rounded-xl border border-border px-4 py-3 text-sm">
              <p className="font-medium text-foreground">{label}</p>
              <p className="mt-1 text-muted-foreground">
                This will email the donor with the pickup date and note.
              </p>
              {row.email && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Email: {row.email}
                </p>
              )}
              {[row.pickup_date, row.pickup_note].some(Boolean) && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Last sent
                  {row.pickup_date ? `: ${formatDate(row.pickup_date)}` : ""}
                  {row.pickup_note ? ` · ${row.pickup_note}` : ""}
                </p>
              )}
            </div>

            <div>
              <Label>Pickup Date *</Label>
              <Input
                type="date"
                value={pickupDate}
                onChange={e => setPickupDate(e.target.value)}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label>Pickup Note *</Label>
              <Textarea
                rows={3}
                value={pickupNote}
                onChange={e => setPickupNote(e.target.value)}
                placeholder="Our team will visit your address between 10 AM and 1 PM."
                className="mt-1.5"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              disabled={acting}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={acting}
              className="rounded-xl bg-sky-600 hover:bg-sky-700"
              onClick={() => void confirm()}
            >
              {acting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Send Email"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
